import { useState, useCallback } from "react";
import {
  CookieCategory,
  DEFAULT_CONSENT,
  getStoredConsent,
  storeConsent,
  initSentryIfConsented,
} from "@/lib/cookieConsent";

export function useCookieConsent() {
  const stored = getStoredConsent();
  const [isVisible, setIsVisible] = useState(stored === null);
  const [analytics, setAnalytics] = useState(stored?.analytics ?? DEFAULT_CONSENT.analytics);
  const [personalization, setPersonalization] = useState(
    stored?.personalization ?? DEFAULT_CONSENT.personalization,
  );

  const persist = useCallback((a: boolean, p: boolean) => {
    storeConsent({ analytics: a, personalization: p });
    if (a) initSentryIfConsented();
    setIsVisible(false);
  }, []);

  const save = useCallback(
    () => persist(analytics, personalization),
    [analytics, personalization, persist],
  );

  const acceptAll = useCallback(() => {
    setAnalytics(true);
    setPersonalization(true);
    persist(true, true);
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    setAnalytics(false);
    setPersonalization(false);
    persist(false, false);
  }, [persist]);

  const updateCategory = useCallback((cat: CookieCategory, value: boolean) => {
    if (cat === "analytics") setAnalytics(value);
    if (cat === "personalization") setPersonalization(value);
  }, []);

  /** Allow re-opening the banner from e.g. the footer / privacy page */
  const openBanner = useCallback(() => setIsVisible(true), []);

  return {
    isVisible,
    analytics,
    personalization,
    save,
    acceptAll,
    rejectNonEssential,
    updateCategory,
    openBanner,
  };
}
